// ÁTTEKINTÉS — KPI-sor: a napi tájékozódás első pillantása.
//
// MINDEN KPI JOG-SZŰRT: a kártya csak akkor renderel, ha a hívó jogosult (a route dönt) ÉS van értéke. Jog
// nélkül nincs „tiltott" kártya, nincs `—` — a kártya nincs ott (a lekérdezés sem indult el).
//
// A KPI a lista-oldalra VISZ (a szám nem zsákutca): a kártya egésze link, a fókusz-ring a kártyán jelenik meg.
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon } from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import { type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { formatMoneyShort } from '@/lib/format-money';

/** KPI-kártya: szám + ikon, a teljes kártya link a lista-oldalra. */
export function KpiCard({
  to,
  search,
  label,
  value,
  icon: Icon,
}: {
  to: string;
  search?: Record<string, string>;
  label: string;
  value: number;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}): JSX.Element {
  return (
    <Link
      // A típusos router-link `to`-ja szűkebb, mint a `string`; a kártya-készlet viszont adat-vezérelt
      // (jog-szűrt lista), ezért itt egy cast áll — a cél-útvonalak a hívóban literálok.
      to={to as never}
      search={search as never}
      className="focus-visible:ring-ring group block rounded-xl focus-visible:outline-none focus-visible:ring-2"
    >
      <Card className="hover:border-primary/40 h-full transition-colors">
        <CardContent className="flex items-center justify-between gap-4">
          <div>
            <div className="text-muted-foreground text-sm font-medium">{label}</div>
            <div className="mt-1 text-3xl font-semibold tabular-nums tracking-tight">{value}</div>
          </div>
          <div className="bg-muted text-muted-foreground group-hover:text-primary flex h-11 w-11 items-center justify-center rounded-xl transition-colors">
            <Icon className="h-5 w-5" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Mini trend-vonal (sparkline) — INLINE SVG, szándékosan chart-lib NÉLKÜL: egy 12 pontos, 28 px magas vonalhoz
 * a `recharts` konténer-mérés és tengely-apparátus túl sok (a nagy chartok maradnak a bevétel-riporton).
 * A11y: a grafika `aria-hidden`, a tartalmát a kártya szöveges értéke és a változás-badge hordozza.
 */
function Sparkline({ points }: { points: number[] }): JSX.Element | null {
  if (points.length < 2) return null;
  const width = 96;
  const height = 28;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points.map((v, i) => {
    const x = i * step;
    // A 2 px-es belső margó azért van, hogy a vonal-vég ne vágódjon le a viewBox szélén.
    const y = height - 2 - ((v - min) / span) * (height - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="text-brand h-7 w-full"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden
      focusable="false"
    >
      <polyline
        points={coords.join(' ')}
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * MRR-KPI: a havi ismétlődő bevétel + az előző hónaphoz mért változás + 12 hónap trend.
 *
 * Az összeg RÖVIDÍTETT alakban (`formatMoneyShort`) — a KPI-kártyán a nagyságrend a lényeg, a pontos érték a
 * bevétel-riporton van (oda is visz a kártya). A `finance.revenue.view` jog mögött él.
 */
export function MrrKpiCard({
  mrr,
  currency,
  changePct,
  trend,
}: {
  mrr: string;
  currency: string;
  changePct: number | null;
  trend: { month: string; amount: string }[];
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const points = trend.map((p) => Number.parseFloat(p.amount)).filter((n) => Number.isFinite(n));
  const up = changePct !== null && changePct > 0;
  const down = changePct !== null && changePct < 0;
  const pct =
    changePct === null
      ? null
      : `${up ? '+' : ''}${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(changePct)}%`;

  return (
    <Link
      to="/finance"
      className="focus-visible:ring-ring group block rounded-xl focus-visible:outline-none focus-visible:ring-2"
    >
      <Card className="hover:border-primary/40 h-full transition-colors">
        {/* EGYMÁS ALATT, nem egymás mellett: a negyed-szélességű KPI-cellában a szám melletti trendvonal két sorba
            törte az összeget („67,6 M / HUF"), és az egész KPI-sort megemelte. Így a szám egy sorban marad, a vonal
            saját sorban, a kártya alján, teljes szélességben. */}
        <CardContent className="flex flex-col gap-1">
          <div className="text-muted-foreground truncate text-sm font-medium">
            {t('dashboard.kpi.mrr')}
          </div>
          <div className="whitespace-nowrap text-2xl font-semibold tabular-nums tracking-tight">
            {formatMoneyShort(mrr, currency, i18n.language)}
          </div>
          {pct !== null && (
            <div
              className={`inline-flex min-w-0 items-center gap-1 text-xs font-medium tabular-nums ${
                up ? 'text-success' : down ? 'text-danger-600' : 'text-muted-foreground'
              }`}
            >
              {up ? (
                <ArrowTrendingUpIcon className="size-3.5 shrink-0" aria-hidden />
              ) : down ? (
                <ArrowTrendingDownIcon className="size-3.5 shrink-0" aria-hidden />
              ) : null}
              <span className="truncate">{t('dashboard.kpi.mrrChange', { pct })}</span>
            </div>
          )}
          {/* a trend-vonal SAJÁT sorban, a kártya teljes szélességében — a negyed-cellában a szöveg mellett kilógott */}
          <Sparkline points={points} />
        </CardContent>
      </Card>
    </Link>
  );
}
